import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ChevronDown, CheckCircle2, Send, XCircle, FileText, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

interface InvoiceStatusDropdownProps {
  invoiceId: string;
  currentStatus: string;
  invoiceNumber: string;
}

const statusOptions = [
  { value: "draft", label: "Draft", icon: FileText, color: "text-gray-600" },
  { value: "sent", label: "Sent", icon: Send, color: "text-blue-600" },
  { value: "paid", label: "Paid", icon: CheckCircle2, color: "text-green-600" },
  { value: "overdue", label: "Overdue", icon: AlertCircle, color: "text-red-600" },
  { value: "cancelled", label: "Cancelled", icon: XCircle, color: "text-gray-600" },
];

export function InvoiceStatusDropdown({ 
  invoiceId, 
  currentStatus, 
  invoiceNumber 
}: InvoiceStatusDropdownProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);

  const currentStatusOption = statusOptions.find(opt => opt.value === currentStatus);
  const CurrentIcon = currentStatusOption?.icon || FileText;

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === currentStatus) return;
    
    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from("invoices")
        .update({ status: newStatus })
        .eq("id", invoiceId);

      if (error) throw error;

      toast({
        title: "Status Updated",
        description: `Invoice ${invoiceNumber} status changed to ${newStatus}`,
      });

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["invoices-analytics"] });
      queryClient.invalidateQueries({ queryKey: ["revenue-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["payment-invoices"] });
      
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to update status: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          disabled={isUpdating}
          className="min-w-[120px]"
        >
          <CurrentIcon className={`h-4 w-4 mr-2 ${currentStatusOption?.color}`} />
          {currentStatusOption?.label || "Unknown"}
          <ChevronDown className="h-4 w-4 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {statusOptions.map((option) => {
          const Icon = option.icon;
          const isActive = option.value === currentStatus;
          
          return (
            <DropdownMenuItem
              key={option.value}
              onClick={() => handleStatusChange(option.value)}
              disabled={isActive}
              className="cursor-pointer"
            >
              <Icon className={`h-4 w-4 mr-2 ${option.color}`} />
              <span className={isActive ? "font-semibold" : ""}>
                {option.label}
              </span>
              {isActive && (
                <CheckCircle2 className="h-4 w-4 ml-auto text-primary" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
